import { MediaQuery } from "@outplayed/responsive";
import { useSummonerProfileContext } from "@ugg/shared/components/SummonerProfiles/SummonerProfileContext";
import { selectRank } from "@ugg/shared/utils/rank-helpers";
import { QueueTypeN } from "@ugg/shared/utils/queue-type-helpers";
import { INTERNAL_CURRENT_SEASON } from "@ugg/shared/utils/season-helpers";
import { useSummonerProfileInitSimple } from "@ugg/shared/api/requests/summoner-profiles/profile-init";
import { HistoricRanks } from "@ugg/shared/components/common/HistoricRanks/HistoricRanks";
import { ProfileIconContainer } from "@ugg/shared/components/SummonerProfiles/profile-header/ProfileIconContainer";
import { LadderRanking } from "@ugg/shared/components/SummonerProfiles/profile-header/LadderRanking";
import { HeaderUpdateButton } from "@ugg/shared/components/SummonerProfiles/profile-header/HeaderUpdateButton";
import { MemberStatus } from "@ugg/shared/components/SummonerProfiles/profile-header/MemberStatus";
import { UggLink } from "@ugitgud/legos/ui/nav/Link";
import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { SvgLolTwitchIcon } from "@ugitgud/legos/ui/icon/SVG/LolTwitchIcon";
import { SvgLolYoutubeIcon } from "@ugitgud/legos/ui/icon/SVG/LolYoutubeIcon";
import { SvgLolXIcon } from "@ugitgud/legos/ui/icon/SVG/LolXIcon";
import { useRecurlySubscriptionStatus } from "@ugitgud/legos/hooks/usePremiumHooks";

export function SummonerProfileHeader() {
  const context = useSummonerProfileContext();
  const { riotUserName, riotTagLine, region } = context;

  const {
    data: profileData,
    loading: loadingProfile,
    error: errorProfile,
  } = useSummonerProfileInitSimple(region, riotUserName, riotTagLine, {});

  if (!profileData || loadingProfile || errorProfile) {
    return null;
  }

  const { profileInitSimple, fetchProfileRanks } = profileData;
  const { playerInfo, memberStatus, lastModified, customizationData, premium: isPremium } = profileInitSimple || {};
  const { rankScores } = fetchProfileRanks || {};
  const { riotUserName: realRiotUserName, riotTagLine: realRiotTagLine, regionId, iconId, summonerLevel } = playerInfo || {};

  const rankToDisplay = selectRank("all", INTERNAL_CURRENT_SEASON, rankScores);
  const { tier } = rankToDisplay || {};

  const { twitchName, youtubeName, twitterName } = customizationData || {};

  return (
    <div className="pt-[24px]">
      <HistoricRanks
        className="mb-[24px]"
        riotUserName={riotUserName}
        riotTagLine={riotTagLine}
        regionId={region}
        queueType={QueueTypeN.RANKED_SOLO}
      />
      <div className="flex items-end flex-1">
        <ProfileIconContainer tier={tier} summonerLevel={summonerLevel} iconId={iconId} isPremium={isPremium} />
        <div className={"flex flex-col justify-between flex-1 ml-[24px] max-xs:ml-[12px] min-w-0"}>
          <div className="mb-[8px] flex items-center overflow-hidden">
            <div className="flex items-baseline font-['Barlow'] max-sm:text-[24px] text-[36px] font-semibold overflow-hidden">
              <span className={`leading-[normal] max-w-[20ch] truncate ${isPremium ? "text-accent-yellow-400" : ""}`}>
                {realRiotUserName}
              </span>
              <span className="text-lavender-50 font-normal ml-[4px] max-sm:text-[14px] text-[22px] leading-[normal]">
                #{realRiotTagLine}
              </span>
            </div>
            {process.env.UGG_PUBLIC_PLATFORM !== "app" && (
              <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE">
                <div className="flex self-end ml-[10px] max-xs:ml-[8px]">
                  <MemberStatus status={memberStatus} riotUserName={realRiotUserName} riotTagLine={realRiotTagLine} />
                </div>
              </MediaQuery>
            )}
          </div>
          <LadderRanking
            className="mt-[-8px] mb-[8px]"
            riotUserName={riotUserName}
            riotTagLine={riotTagLine}
            region={region}
            queueType={QueueTypeN.RANKED_SOLO}
          />
          <div className="flex justify-start">
            <HeaderUpdateButton lastModified={lastModified} />
            {isPremium && twitchName && (
              <UggLink
                href={`https://www.twitch.tv/${twitchName}`}
                className="bg-purple-500 hover:bg-purple-900 rounded-[4px] ml-[12px] h-[36px] w-[36px] cursor-pointer flex items-center justify-center"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon icon={<SvgLolTwitchIcon />} className="w-[20px] h-[20px]" />
              </UggLink>
            )}
            {isPremium && youtubeName && (
              <UggLink
                href={`https://www.youtube.com/@${youtubeName}`}
                className="bg-purple-500 hover:bg-purple-900 rounded-[4px] ml-[12px] h-[36px] w-[36px] cursor-pointer flex items-center justify-center"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon icon={<SvgLolYoutubeIcon />} className="w-[20px] h-[20px]" />
              </UggLink>
            )}
            {isPremium && twitterName && (
              <UggLink
                href={`https://x.com/${twitterName}`}
                className="bg-purple-500 hover:bg-purple-900 rounded-[4px] ml-[12px] h-[36px] w-[36px] cursor-pointer flex items-center justify-center"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon icon={<SvgLolXIcon />} className="w-[20px] h-[20px]" />
              </UggLink>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
